import React, { useCallback, useEffect, useMemo, useState } from "react";
import useCurrentProject, { resolveProject } from "../Common Components/useCurrentProject";
import { whatsNewApi } from "./whatsNewApi";

/**
 * Pick sprint tickets to add to a release draft.
 *
 * Any ticket may be drafted, shipped or not — the copy can be written while the
 * work is still in UAT. Unshipped tickets carry a note saying they will hold up
 * "Send to clients"; the server enforces that at send time, against the
 * ticket's status then, so a drafted ticket becomes sendable once it ships.
 */
export default function AddUpdatesModal({ releaseId, onClose, onAdded, onError }) {
    // Seeded from the app-wide project selection, but never dependent on it: a
    // release is often prepared without visiting the sprint board first, and
    // sending someone away to another screen to set an invisible bit of state
    // is not a reasonable thing to ask.
    const [currentProject, setCurrentProject] = useCurrentProject();
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState(currentProject || "");

    const [sprints, setSprints] = useState([]);
    const [sprintId, setSprintId] = useState("");
    const [tickets, setTickets] = useState([]);
    const [allowed, setAllowed] = useState([]);
    const [picked, setPicked] = useState(() => new Set());
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    // Load the project list, then settle on a selection: keep the shared one if
    // it is still valid, otherwise adopt the first. Same rule the sprint board
    // uses, so the two screens never disagree about which project is current.
    useEffect(() => {
        (async () => {
            try {
                const res = await whatsNewApi.getProjects();
                const list = res.data || res || [];
                const arr = Array.isArray(list) ? list : [];
                setProjects(arr);
                const resolved = resolveProject(arr, currentProject);
                if (resolved) {
                    setProjectId(String(resolved));
                    setCurrentProject(resolved);
                }
            } catch (e) {
                setErr(e.message || "Could not load projects.");
                setProjects([]);
            }
        })();
        // Runs once on open; `currentProject` is only the initial seed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!projectId) {
            setSprints([]);
            return;
        }
        (async () => {
            try {
                const res = await whatsNewApi.getSprints(projectId);
                const list = res.data || res || [];
                setSprints(Array.isArray(list) ? list : []);
            } catch {
                setSprints([]);
            }
        })();
        setSprintId("");
    }, [projectId]);

    const loadTickets = useCallback(async (id) => {
        if (!id) {
            setTickets([]);
            return;
        }
        setLoading(true);
        setErr("");
        setPicked(new Set());
        setQ("");
        try {
            const res = await whatsNewApi.getShareableTickets(id);
            setTickets(res.data || []);
            setAllowed(res.shareable_statuses || []);
        } catch (e) {
            setErr(e.message || "Could not load tickets.");
            setTickets([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTickets(sprintId);
    }, [sprintId, loadTickets]);

    const selectable = useMemo(
        () => tickets.filter((t) => !t.already_shared),
        [tickets]
    );
    const pickedUnshipped = useMemo(
        () => selectable.filter((t) => !t.is_shareable && picked.has(t.id)).length,
        [selectable, picked]
    );

    // Search narrows what is shown and what the bulk buttons act on; ticks on
    // tickets hidden by the search are kept.
    const visible = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return tickets;
        return tickets.filter((t) =>
            [t.ticket_ref, t.description, t.status, t.client_name]
                .some((v) => (v || "").toLowerCase().includes(needle))
        );
    }, [tickets, q]);
    const visibleSelectable = useMemo(
        () => visible.filter((t) => !t.already_shared),
        [visible]
    );
    const visibleShipped = useMemo(
        () => visibleSelectable.filter((t) => t.is_shareable),
        [visibleSelectable]
    );
    const allVisiblePicked =
        visibleSelectable.length > 0 && visibleSelectable.every((t) => picked.has(t.id));

    const toggle = (id) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleAll = (on) =>
        setPicked((prev) => {
            const next = new Set(prev);
            visibleSelectable.forEach((t) => (on ? next.add(t.id) : next.delete(t.id)));
            return next;
        });

    const pickShippedOnly = () =>
        setPicked((prev) => {
            const next = new Set(prev);
            visibleSelectable.forEach((t) => next.delete(t.id));
            visibleShipped.forEach((t) => next.add(t.id));
            return next;
        });

    const submit = async () => {
        if (!picked.size) return;
        setSaving(true);
        try {
            const res = await whatsNewApi.addItems(releaseId, [...picked]);
            onAdded(res.data || [], res.message || "");
        } catch (e) {
            onError(e.message || "Could not add updates.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="wn-ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="wn-modal wn-modal--wide" role="dialog" aria-label="Add updates">
                <div className="wn-m-head">
                    <h3>Add updates from a sprint</h3>
                    <button className="wn-x" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="wn-m-body">
                    <div className="wn-row">
                        <div>
                            <label className="wn-label" htmlFor="wn-project">Project</label>
                            <select
                                id="wn-project"
                                className="wn-select"
                                value={projectId}
                                onChange={(e) => {
                                    setProjectId(e.target.value);
                                    // Share the choice with the rest of the app,
                                    // the same way the sprint board does.
                                    setCurrentProject(e.target.value);
                                }}
                            >
                                <option value="">Select a project…</option>
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.project_name || p.name || `Project ${p.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="wn-label" htmlFor="wn-sprint">Sprint</label>
                            <select
                                id="wn-sprint"
                                className="wn-select"
                                value={sprintId}
                                onChange={(e) => setSprintId(e.target.value)}
                                disabled={!projectId}
                            >
                                <option value="">
                                    {projectId ? "Select a sprint…" : "Pick a project first"}
                                </option>
                                {sprints.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.sprint_name || s.name || `Sprint ${s.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {projectId && sprints.length === 0 && (
                        <div className="wn-hint">
                            No sprints found for this project.
                        </div>
                    )}

                    {err && <div className="wn-hint wn-hint--warn">{err}</div>}

                    {loading && <div style={{ padding: 16, fontSize: 13 }}>Loading tickets…</div>}

                    {!loading && sprintId && tickets.length === 0 && !err && (
                        <div className="wn-empty">No tickets in this sprint.</div>
                    )}

                    {!loading && tickets.length > 0 && (
                        <>
                            <div
                                className="wn-panel-top"
                                style={{ marginTop: 14, paddingLeft: 0, paddingRight: 0, border: 0 }}
                            >
                                <div className="wn-count">
                                    {picked.size} of {selectable.length} selectable chosen
                                </div>
                                <button
                                    className="wn-btn wn-btn--sm"
                                    onClick={pickShippedOnly}
                                    disabled={!visibleShipped.length}
                                >
                                    Select shipped only
                                </button>
                                <button
                                    className="wn-btn wn-btn--sm"
                                    onClick={() => toggleAll(!allVisiblePicked)}
                                    disabled={!visibleSelectable.length}
                                >
                                    {allVisiblePicked ? "Clear all" : "Select all"}
                                </button>
                            </div>

                            <input
                                type="search"
                                className="wn-input"
                                style={{ marginBottom: 10 }}
                                placeholder="Search by ticket no., description, status or client…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                aria-label="Search tickets"
                            />

                            {visible.length === 0 && (
                                <div className="wn-empty">No tickets match “{q.trim()}”.</div>
                            )}

                            <div className="wn-panel">
                                {visible.map((t) => {
                                    const blocked = t.already_shared;
                                    return (
                                        <div
                                            key={t.id}
                                            className={`wn-pick ${blocked ? "wn-pick--blocked" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={picked.has(t.id)}
                                                onChange={() => toggle(t.id)}
                                                disabled={blocked}
                                                aria-label={`Select ${t.ticket_ref}`}
                                            />
                                            <div className="wn-pick-main">
                                                <div className="wn-pick-desc">{t.description || "(no description)"}</div>
                                                <div className="wn-pick-meta">
                                                    <span className="wn-item-ref">{t.ticket_ref}</span>
                                                    <span>{t.status || "no status"}</span>
                                                    {t.client_name && <span>· {t.client_name}</span>}
                                                    {t.suggested_orgs?.length > 0 && (
                                                        <span>
                                                            · {t.suggested_orgs.length} org
                                                            {t.suggested_orgs.length === 1 ? "" : "s"} will be pre-selected
                                                        </span>
                                                    )}
                                                </div>
                                                {t.already_shared && (
                                                    <div className="wn-pick-blocked">
                                                        Already included in a release.
                                                    </div>
                                                )}
                                                {!t.is_shareable && t.blocked_reason && (
                                                    <div className="wn-pick-blocked">{t.blocked_reason}</div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {allowed.length > 0 && (
                                <div className="wn-hint" style={{ marginTop: 10 }}>
                                    Any ticket can be drafted, but the release can only be sent to
                                    clients once every update is in {allowed.join(", ")}. Change
                                    this list under the company&apos;s release-eligible statuses.
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="wn-m-foot">
                    <div className="wn-sp">
                        {picked.size > 0 && (
                            <span className="wn-hint">
                                Each added update needs a customer-facing sentence before it
                                can be published.
                                {pickedUnshipped > 0 &&
                                    ` ${pickedUnshipped} not yet in production — sending will be blocked until they ship.`}
                            </span>
                        )}
                    </div>
                    <button className="wn-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="wn-btn wn-btn--primary"
                        onClick={submit}
                        disabled={saving || !picked.size}
                    >
                        {saving ? "Adding…" : `Add ${picked.size || ""} update${picked.size === 1 ? "" : "s"}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
